import { IOrder, IOrderLineItem, IProductVariant } from "shopify-api-node";
import { ExchangeModalProps } from "./BookingDetailsModal";
import React, {
  Fragment,
  useCallback,
  useMemo,
  useState,
} from "react";
import ModalSection from "../../components/Modal/ModalSection";
import Modal from "../../components/Shared/Modal";
import ToggleSwitch from "../../components/Shared/ToggleSwitch";
import TextAreaInput from "../../components/Shared/TextAreaInput";
import Button from "../../components/Shared/Button";
import ModalNavigation from "../../components/Modal/ModalNavigation";
import { TBookingType } from "../../interfaces";
import ModalTrackExperienceSelector from "../../components/Modal/ModalTrackExperienceSelector";
import Checkbox from "../../components/Shared/Checkbox";
import { CheckIcon, ClockIcon } from "@heroicons/react/solid";
import axios from "axios";
import { useBookingSession } from "../../context/BookingSession";
import { getRowBgColor } from "../../utilities";
import TextInput from "../../components/Shared/TextInput";
import { useSession } from "../../components/Session";
import BookingSelection from "../../components/BookingSelection";

type TExchangeItem = Record<string, number>;
type TReturnItem = Record<string, number>;
type TLineItem = Record<string, IOrderLineItem>;

function Exchange({order, show, handleClose}: ExchangeModalProps) {
  const [returnItems, setReturnItems] = useState<TExchangeItem>(order.line_items.filter(li => (li as any).current_quantity > 0).reduce((acc, ele) => ({ ...acc, [ele.id]: 0 }), {}));
  const [exchangeItems, setExchangeItems] = useState<TExchangeItem>({});
  const [isRestockAtLocation, setIsRestockAtLocation] = useState(true);
  const [reasonForExchange, setReasonForExchange] = useState("");
  const [waivePriceDifference, setWaivePriceDifference] = useState(false);
  const [reasonForWaivingPrice, setReasonForWaivingPrice] = useState("");
  const [selectedVariants, setSelectedVariants] = useState<IProductVariant[]>([]);
  const [exchangedBy, setExchangedBy] = useState("");
  const [screen, setScreen] = useState<("SELECT_RETURNS" | "SELECT_EXCHANGES" | "EXCHANGE_RESULT")>("SELECT_RETURNS");
  const [sendEmailToCustomer, setSendEmailToCustomer] = useState(false);

  const { refreshSessions } = useBookingSession();
  const { getToken } = useSession();

  const lineItems: TLineItem = useMemo(() => order.line_items.filter(li => (li as any).current_quantity > 0).reduce((acc, ele) => ({ ...acc, [ele.id]: ele }), {}), [order]);

  const newItemsCount = useMemo(()=>Object.values(exchangeItems).reduce((total, quantity)=>total+quantity,0),[exchangeItems])
  const returnItemsCount = useMemo(()=>Object.values(returnItems).reduce((total, quantity)=>total+quantity,0),[returnItems])

  const returnSubTotal = useMemo(() =>
    Object.keys(returnItems).reduce(
      (total, id) =>
        total + getDiscountedPrice(lineItems[id], returnItems[id]),
      0
    ),
  [returnItems, lineItems]);

  const exchangeSubTotal = useMemo(() => Object.keys(exchangeItems).reduce((total, sku)=>{
    const variant = selectedVariants.find(v => v.sku === sku);
    const amount = variant ? Number(variant.price) * exchangeItems[sku] : 0;
    return total + amount;
  },0),[exchangeItems, selectedVariants]);

  const doExchange = useCallback(async () => {
    console.log("[Do Exchange] ", exchangeItems, returnItems, isRestockAtLocation, reasonForExchange, waivePriceDifference, reasonForWaivingPrice, selectedVariants);
    const exchanges = {
      orderId: order.id.toString(),
      returnItems: Object.keys(returnItems).map(id => ({
        variantId: lineItems[id].variant_id,
        quantity: returnItems[id]
      })).filter(e=>e.variantId && e.quantity > 0),
      exchangeItems: Object.keys(exchangeItems).map(sku => ({
        variantId: selectedVariants.find(v => v.sku === sku)?.id,
        quantity: exchangeItems[sku]
      })).filter(e=>e.variantId && e.quantity > 0),
      isRestockAtLocation: isRestockAtLocation,
      waivePriceDifference: waivePriceDifference,
      reasonForExchange: reasonForExchange,
      reasonForWaivingPrice: reasonForWaivingPrice,
      exchangedBy: exchangedBy,
      sendEmailToCustomer
    }
    console.log("exchanges", exchanges)

    const token = await getToken();

    // const url = "http://127.0.0.1:5001/hyper-karting/us-central1/exchangeSessions"
    const url = "https://us-central1-hyper-karting.cloudfunctions.net/exchangeSessions"

    const response = await axios.post(url, {
      version: "v2",
      exchanges
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    console.log("response.data", response.data)

    return response.data

  }, [exchangeItems, returnItems, isRestockAtLocation, reasonForExchange, waivePriceDifference, reasonForWaivingPrice, selectedVariants, lineItems, order, exchangedBy, sendEmailToCustomer, getToken]);

  const onCloseModal = useCallback(async () => {
    await refreshSessions();
    handleClose();
  }, [refreshSessions, handleClose]);

  if(!show){
    return (
      <></>
    )
  }

  if(screen === "SELECT_RETURNS" || screen === "SELECT_EXCHANGES"){
    return (
      <Modal
        title="Exchange"
        show={show}
        onClose={onCloseModal}
        className="max-w-xl max-h-[1200px]"
      >
        {screen === "SELECT_RETURNS" ? (
          <SelectReturns 
            lineItems={lineItems} 
            returnItems={returnItems} 
            isRestockAtLocation={isRestockAtLocation} 
            setIsRestockAtLocation={setIsRestockAtLocation} 
            setReturnItems={setReturnItems} 
            returnSubTotal={returnSubTotal}
            onConfirm={() => setScreen("SELECT_EXCHANGES")}
          />
        ) : (
          <SelectExchanges 
            exchangeItems={exchangeItems}
            reasonForExchange={reasonForExchange}
            setReasonForExchange={setReasonForExchange}
            setExchangeItems={setExchangeItems}
            selectedVariants={selectedVariants}
            setSelectedVariants={setSelectedVariants}
            waivePriceDifference={waivePriceDifference}
            setWaivePriceDifference={setWaivePriceDifference}
            reasonForWaivingPrice={reasonForWaivingPrice}
            setReasonForWaivingPrice={setReasonForWaivingPrice}
            exchangedBy={exchangedBy}
            setExchangedBy={setExchangedBy}
            returnSubTotal={returnSubTotal}
            exchangeSubTotal={exchangeSubTotal}
            onConfirm={doExchange}
            onPrev={() => setScreen("SELECT_RETURNS")}
            onSuccess={() => setScreen("EXCHANGE_RESULT")}
            sendEmailToCustomer={sendEmailToCustomer}
            setSendEmailToCustomer={setSendEmailToCustomer}
            newItemsCount={newItemsCount}
            returnItemsCount={returnItemsCount}
          />
        )}
      </Modal>
    )
  }else if(screen === "EXCHANGE_RESULT"){
    return (
      <Modal
        show={show}
        onClose={onCloseModal}
        className="max-w-xl"
      >
        <ExchangeResult 
          order={order} 
          returnSubTotal={returnSubTotal}
          exchangeSubTotal={exchangeSubTotal}
          waivePriceDifference={waivePriceDifference}
          sendEmailToCustomer={sendEmailToCustomer}
          returnItemsCount={returnItemsCount}
        />
      </Modal>
    )
  }
  return <></>;
}

const SelectReturns = ({ lineItems, returnItems, isRestockAtLocation, returnSubTotal, setIsRestockAtLocation, setReturnItems, onConfirm } : { lineItems: TLineItem, returnItems: TReturnItem, isRestockAtLocation: boolean, returnSubTotal: number, setIsRestockAtLocation: React.Dispatch<React.SetStateAction<boolean>>, setReturnItems: React.Dispatch<React.SetStateAction<TReturnItem>>, onConfirm: () => void }) => {
  const incrementQuantity = useCallback((id: number) => () => {
    setReturnItems((old) => ({
      ...old,
      [id]: Math.min(old[id] + 1, lineItems[id].quantity),
    }));
  },[lineItems, setReturnItems]);

  const decrementQuantity = useCallback((id: number) => () => {
    setReturnItems((old) => ({
      ...old,
      [id]: Math.max(old[id] - 1, 0),
    }));
  },[setReturnItems]);

  const selectedItems = useMemo(() =>
    Object.keys(returnItems)
      .filter((id) => returnItems[id] > 0)
      .map((id) => ({
        id,
        li: lineItems[id],
        quantity: returnItems[id],
      })),
  [returnItems, lineItems]);

  const disableNext = useMemo(() => Object.values(returnItems).every(item => item === 0), [returnItems]);

  return (
    <div className="flex mt-2 justify-between p-5 gap-6 max-h-[1100px] overflow-hidden">
      <div className="w-[70%] overflow-y-auto overflow-x-hidden">
        <ModalSection title="Current Sessions">
          <div className="flex flex-col gap-2 max-h-[500px] overflow-y-auto">
            {Object.values(lineItems).map((item, index) => {
              return (
                <Fragment key={item.id}>
                  <div className={`flex flex-col rounded-lg border border-gray-200 mb-2 ${getRowBgColor(item)}`}>
                    <div className={`p-4 flex justify-between items-center`}>
                      <div className="flex w-[70%] items-center">
                        <div className="font-bold text-xs leading-4 text-gray-700 mr-2">
                          #{index + 1}
                        </div>
                        <div>
                          <div className="font-medium text-xs leading-4 text-gray-700">
                            {item.title}
                          </div>
                          <div className="font-medium text-[0.5rem] leading-tight text-gray-500">
                            {item.variant_title}
                          </div>
                          <div className="font-medium text-[0.5rem] leading-tight text-gray-500">
                            SKU: {item.sku}
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="flex gap-3 bg-white font-medium text-xs leading-none text-gray-700 items-center justify-center p-1 rounded-full">
                          <div
                            className={`text-base px-2 ${
                              returnItems[item.id] === 0
                                ? "text-gray-200 pointer-events-none"
                                : "cursor-pointer text-gray-700"
                            }`}
                            onClick={decrementQuantity(+item.id)}
                          >
                            -
                          </div>
                          <div>
                            {returnItems[item.id]}/{(item as any).current_quantity}
                          </div>
                          <div
                            className={`text-base px-2 ${
                              returnItems[item.id] >= (item as any).current_quantity
                                ? "text-gray-200 pointer-events-none"
                                : "cursor-pointer text-gray-700"
                            }`}
                            onClick={incrementQuantity(+item.id)}
                          >
                            +
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="font-bold text-xs leading-none text-gray-700">
                          ${Number(item.price).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </Fragment>
              );
            })}
          </div>
        </ModalSection>

        <div className="w-full flex items-center gap-2 mt-4 justify-between">
          <div className="flex items-center gap-2">
            <ToggleSwitch
              defaultValue={isRestockAtLocation}
              onChange={setIsRestockAtLocation}
              variant="large"
            />
            <span className="text-xs font-normal leading-[11px] text-gray-700">
              Restock At This Location
            </span>
          </div>
        </div>
      </div>

      <div className="w-[40%] flex flex-col">
        <ModalSection title="Summary">
          <div className="flex flex-col space-y-2">
            <div className="flex justify-between">
              <span className="text-sm font-semibold text-gray-700">
                Items Subtotal:
              </span>
              <span className="text-sm font-semibold text-gray-700">
                ${returnSubTotal.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs font-normal text-gray-700">
                {selectedItems.reduce(
                  (total, item) => total + item.quantity,
                  0
                )}{" "}
                items
              </span>
            </div>
            <div className="border-t border-gray-300 my-1"></div>
            <div className="flex justify-between">
              <span className="text-sm font-semibold text-gray-700">
                Exchange Subtotal:
              </span>
              <span className="text-sm font-semibold text-gray-700">
                ${returnSubTotal.toFixed(2)}
              </span>
            </div>
          </div>
        </ModalSection>
        <div className="mt-auto ml-auto pt-2">
          <ModalNavigation disablePrev disableNext={disableNext} onNext={onConfirm} />
        </div>
      </div>
    </div>
  )
};

const SelectExchanges = ({ exchangeItems, reasonForExchange, returnSubTotal, exchangeSubTotal, selectedVariants, reasonForWaivingPrice, waivePriceDifference, sendEmailToCustomer, newItemsCount, returnItemsCount, setSendEmailToCustomer, setReasonForWaivingPrice, setWaivePriceDifference, setSelectedVariants, setReasonForExchange, setExchangeItems, exchangedBy, setExchangedBy, onConfirm, onPrev, onSuccess }: { exchangeItems: TExchangeItem, reasonForExchange: string, returnSubTotal: number, exchangeSubTotal: number, selectedVariants: IProductVariant[], reasonForWaivingPrice: string, waivePriceDifference: boolean, sendEmailToCustomer: boolean, newItemsCount: number, returnItemsCount:number, setSendEmailToCustomer: React.Dispatch<React.SetStateAction<boolean>>, setReasonForWaivingPrice: React.Dispatch<React.SetStateAction<string>>, setWaivePriceDifference: React.Dispatch<React.SetStateAction<boolean>>, setSelectedVariants: React.Dispatch<React.SetStateAction<IProductVariant[]>>, setReasonForExchange: React.Dispatch<React.SetStateAction<string>>, setExchangeItems: React.Dispatch<React.SetStateAction<TExchangeItem>>, exchangedBy: string, setExchangedBy: React.Dispatch<React.SetStateAction<string>>, onConfirm: () => Promise<any>, onPrev: () => void, onSuccess: () => void }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [track, setTrack] = useState<TBookingType>("MAIN_TRACK");

  const incrementQuantity = useCallback((sku: string) => () => {
    setExchangeItems((old) => ({
      ...old,
      [sku]: old[sku] ? old[sku] + 1 : 1,
    }));
  },[setExchangeItems]);

  const decrementQuantity = useCallback((sku: string) => () => {
    setExchangeItems((old) => ({
      ...old,
      [sku]: Math.max(old[sku] - 1, 0),
    }));
  },[setExchangeItems]);

  const handleTrackSelection = useCallback((track: string) => {
    setTrack(track as TBookingType);
  }, [setTrack]);

  const handleExchange = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await onConfirm();
      console.log("response", response)
      if(response.success){
        onSuccess()
      }else{
        console.log("response.data", response?.data)
        alert(response?.data?.message || response?.data?.error || "something went wrong")
      }
      setIsLoading(false)
    } catch (error) {
      console.log("error", error)
      alert((error as any).response.data.message || (error as any).response.data.error || (error as Error).message ||  "something went wrong")
    }finally{
      setIsLoading(false)
    }
  }, [onConfirm, onSuccess]);

  const disableExchangeButton = useMemo(() => {
    return !exchangeSubTotal || isLoading || !reasonForExchange || (waivePriceDifference && !reasonForWaivingPrice);
  }, [exchangeSubTotal, isLoading, reasonForExchange, waivePriceDifference, reasonForWaivingPrice]);

  const handleSessionClick = useCallback((_: string[], changedVariant: IProductVariant, added: boolean) => {
    setSelectedVariants(existingVariants => {
      return added ? [...existingVariants, changedVariant] : existingVariants.filter(v => v.id !== changedVariant.id);
    });
  }, [setSelectedVariants]);

  return (
    <div className="flex mt-2 justify-between p-5 gap-6 max-h-[1100px] overflow-hidden">
        <div className="w-[70%] overflow-y-auto overflow-x-hidden">
        <ModalSection title="Experience">
          <div className="flex flex-col gap-2">
            <ModalTrackExperienceSelector
              selectedTrack={track}
              onTrackSelect={handleTrackSelection}
            />
            <BookingSelection 
              track={track} 
              onClick={handleSessionClick} 
              selectedVariants={selectedVariants} 
              className="w-full m-auto" 
              allSelectable 
            />
          </div>
        </ModalSection>
        <ModalSection title="New Sessions" className="mt-2">
          <div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto">
            {(selectedVariants).map((variant, index) => {
              return (
                <Fragment key={variant.sku}>
                  <div className="flex flex-col rounded-lg border border-gray-200 mb-2">
                    <div className="p-4 flex justify-between items-center bg-gray-50">
                      <div className="flex w-[70%] items-center">
                        <div className="font-bold text-xs leading-4 text-gray-700 mr-2">
                          #{index + 1}
                        </div>
                        <div>
                          <div className="font-medium text-xs leading-4 text-gray-700">
                            {(variant as any)?.product?.title}
                          </div>
                          <div className="font-medium text-[0.5rem] leading-tight text-gray-500">
                            {variant.title}
                          </div>
                          <div className="font-medium text-[0.5rem] leading-tight text-gray-500">
                            SKU: {variant.sku}
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="flex gap-3 bg-white font-medium text-xs leading-none text-gray-700 items-center justify-center p-1 rounded-full">
                          <div
                            className={`text-base px-2 ${
                              exchangeItems[variant.sku] === 0
                                ? "text-gray-200 pointer-events-none"
                                : "cursor-pointer text-gray-700"
                            }`}
                            onClick={decrementQuantity(variant.sku)}
                          >
                            -
                          </div>
                          <div>
                            {exchangeItems[variant.sku] || 0}
                          </div>
                          <div
                            className={`text-base px-2 ${
                              exchangeItems[variant.sku] === variant.inventory_quantity
                                ? "text-gray-200 pointer-events-none"
                                : "cursor-pointer text-gray-700"
                            }`}
                            onClick={incrementQuantity(variant.sku)}
                          >
                            +
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="font-bold text-xs leading-none text-gray-700">
                          ${((Number(variant.price) * (exchangeItems[variant.sku] || 0))).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </Fragment>
              );
            })}
          </div>
        </ModalSection>
        </div>

        <div className="w-[40%] flex flex-col">
          <ModalSection title="Summary">
            <div className="flex flex-col space-y-2">
              <div className="flex justify-between">
                <span className="text-sm font-semibold text-gray-700">
                  Exchange Subtotal:
                </span>
                <span className="text-sm font-semibold text-gray-700">
                  ${returnSubTotal.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs font-normal text-gray-700">
                  {returnItemsCount} items
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-semibold text-gray-700">
                  New Items Subtotal:
                </span>
                <span className="text-sm font-semibold text-gray-700">
                  ${exchangeSubTotal.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs font-normal text-gray-700">
                  {newItemsCount} items
                </span>
              </div>
            </div>
          </ModalSection>

          <ModalSection title="Notes" className="mt-2">
            <div className="flex flex-col gap-2">
            <TextInput value={exchangedBy} placeholder="Type here..." className={!exchangedBy ? "border-red-600 bg-red-600 bg-opacity-5" : "border-gray-200"} onChange={setExchangedBy} label="Exchanged by" labelColor="text-purple-600"  required/>

              <TextAreaInput
                placeholder="Type here..."
                className={`w-full ${!reasonForExchange ? "border-red-600 bg-red-600 bg-opacity-5" : "border-gray-200"}`}
                value={reasonForExchange}
                onChange={setReasonForExchange}
                required
                label="Reason for Exchange"
              />
              <div className="text-xs text-gray-500 mt-1">
                Only You And Other Staff Can See This Reason.
              </div>
            </div>
          </ModalSection>

          <div className="my-6">
            <Button className="w-full justify-center" onClick={handleExchange} disabled={disableExchangeButton} loading={isLoading}>
              {(waivePriceDifference || (exchangeSubTotal === returnSubTotal)) ? "Process Exchange" : `Create Draft Order ($${(exchangeSubTotal - returnSubTotal).toFixed(2)})`}
            </Button>
          </div>

          <div className="w-full flex items-center gap-2 mt-4 justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={sendEmailToCustomer}
                onChange={setSendEmailToCustomer}
              />
              <span className="text-xs font-normal leading-[11px] text-gray-700">
                Send Email To Customer
              </span>
            </div>
          </div>
          
          <div className="w-full flex items-center gap-2 mt-4 justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={waivePriceDifference}
                onChange={setWaivePriceDifference}
              />
              <span className="text-xs font-normal leading-[11px] text-gray-700">
                Waive Price Difference
              </span>
            </div>
          </div>

          {waivePriceDifference && (
            <ModalSection title="Reason for Waiving Price Difference" className="mt-2">
              <div>
                <TextAreaInput
                  placeholder="Type here..."
                  className={`w-full ${!reasonForWaivingPrice ? "border-red-600 bg-red-600 bg-opacity-5" : "border-gray-200"}`}
                  value={reasonForWaivingPrice}
                  onChange={setReasonForWaivingPrice}
                  required
                  label="Reason"
                />
                <div className="text-xs text-gray-500 mt-1">
                  Only You And Other Staff Can See This Reason.
                </div>
              </div>
            </ModalSection>
          )}

          <div className="mt-auto ml-auto pt-2">
            <ModalNavigation hideNext onPrev={onPrev} disablePrev={isLoading} />
          </div>
        </div>
      </div>
  );
};

const ExchangeResult = ({order, returnSubTotal, exchangeSubTotal, waivePriceDifference, sendEmailToCustomer, returnItemsCount}: {order: IOrder, returnSubTotal: number, exchangeSubTotal: number, waivePriceDifference: boolean, sendEmailToCustomer: boolean, returnItemsCount: number}) => {
  return (
    <div className="flex mt-2 justify-between p-5 gap-6 max-h-[590px] overflow-hidden">
      <div className="flex gap-5 justify-center items-center flex-col w-full">
        {
          waivePriceDifference ? (
            <div className="w-12 h-12 bg-purple-600 rounded-full m-auto flex justify-center items-center">
              <CheckIcon className="text-white p-2" />
            </div>
          ) : (
            <div className="w-12 h-12 bg-purple-600 rounded-full m-auto flex justify-center items-center">
              <ClockIcon className="text-white" />
            </div>
          )
        }

        {
          waivePriceDifference ? (
            <p className="text-[16px] font-semibold leading-normal text-center">
            Exchange for Order {" "}
              <span className="text-purple-600">#{order.name}</span> has been 
              successfully processed.
            </p>
          ) : (
            <p className="text-[16px] font-semibold leading-normal text-center">
            Exchange for Order {" "}
              <span className="text-purple-600">#{order.name}</span> is still pending.
            </p>
          )
        }

        {!waivePriceDifference && (
          <p className="text-[12px] font-normal leading-normal text-gray-700 text-center">
            {sendEmailToCustomer ? "Process the payment through POS or request the customer processes the payment through the paylink in their email." :"Process the payment through draft orders on POS."}
          </p>
        )}

        <ModalSection title="Exchange Details" className="w-full">
          <div className="font-normal text-xs leading-normal flex justify-between mt-2">
            Exchanged Items:
            <span className="font-bold">${returnSubTotal.toFixed(2)}</span>
          </div>
          <div className="font-normal text-[0.6rem] leading-normal flex justify-between text-gray-700">{returnItemsCount} items</div>
          <div className="font-normal text-xs leading-normal flex justify-between mt-2">
            New Items:
            <span className="font-bold">
              ${exchangeSubTotal.toFixed(2)}
            </span>
          </div>
          <div className="font-normal text-xs leading-normal flex justify-between mt-2">
            Exchange difference:
            <span className="font-bold">
              ${(exchangeSubTotal - returnSubTotal).toFixed(2)}
            </span>
          </div>
        </ModalSection>
      </div>
    </div>
  )
}

const getTotalDiscount = (li: IOrderLineItem) =>(li.discount_allocations || []).reduce((total, discount_allocation) => Number(discount_allocation.amount) + total , 0)

const getDiscountedPrice = (li: IOrderLineItem, q: number) => {
  const discount = getTotalDiscount(li);
  const amount = ((+li.price * (li as any).current_quantity - discount) / (li as any).current_quantity) * q;
  return amount;
}

export default Exchange;
